import ROUTE_NAMES from "routes/routeNames";

export type BreadcrumbRoute = {
  label: string;
  description?: string;
  parent?: string;
  listing?: boolean;
  module?: string;
  hide?: boolean;
};

const USER = ROUTE_NAMES.MAIN_ROUTES.USER_MANAGEMENT;

export const BREADCRUMB_ROUTES: Record<string, BreadcrumbRoute> = {
  [ROUTE_NAMES.MAIN_ROUTES.DASHBOARD]: {
    label: "Dashboard",
    description: "Overview of platform activity and metrics",
    listing: true,
  },
  [USER.BASE]: {
    label: "User Management",
    description: "Manage customers, franchises, and partners",
    listing: true,
    module: "user-management",
  },
  [`${USER.BASE}/${USER.CUSTOMERS}`]: {
    label: "Customers",
    description: "View and manage customer accounts",
    parent: USER.BASE,
    listing: true,
    module: "user-management",
  },
  [`${USER.BASE}/${USER.FRANCHISES}`]: {
    label: "Franchises",
    description: "View and manage franchise accounts",
    parent: USER.BASE,
    listing: true,
    module: "user-management",
  },
  [`${USER.BASE}/${USER.COORDINATORS}`]: {
    label: "Coordinators",
    description: "View and manage coordinator accounts",
    parent: USER.BASE,
    listing: true,
    module: "user-management",
  },
  [`${USER.BASE}/${USER.SERVICE_PARTNERS}`]: {
    label: "Service Partners",
    description: "View and manage service partner accounts",
    parent: USER.BASE,
    listing: true,
    module: "user-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.ADD_USER]: {
    label: "Create New User",
    description: "Add a new user to the platform",
    parent: USER.BASE,
    module: "user-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.EDIT_USER]: {
    label: "Edit User",
    description: "Update user information",
    parent: USER.BASE,
    module: "user-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.USER_DETAIL]: {
    label: "User Details",
    description: "View complete user profile",
    parent: USER.BASE,
    module: "user-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.UTILITY_MANAGEMENT]: {
    label: "Utilities Management",
    description: "Manage discounts and car types",
    listing: true,
  },
  [ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT]: {
    label: "Service Management",
    description: "Configure service types and offerings",
    listing: true,
    module: "service-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.ADD_SERVICE]: {
    label: "Add Service Type",
    description: "Create a new service offering",
    parent: ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT,
    module: "service-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL]: {
    label: "Service Details",
    description: "View or edit service type details",
    parent: ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT,
    module: "service-management",
  },
  [ROUTE_NAMES.MAIN_ROUTES.SERVICE_PRICING_MANAGEMENT]: {
    label: "Service Pricing Management",
    description: "Set and update service pricing",
    listing: true,
    module: "service-pricing",
  },
  [ROUTE_NAMES.MAIN_ROUTES.ORDER]: {
    label: "Order Management",
    description: "Track and manage customer orders",
    listing: true,
    module: "order",
  },
  [ROUTE_NAMES.MAIN_ROUTES.CREATE_ORDER]: {
    label: "Create New Order",
    description: "Place a new service order",
    parent: ROUTE_NAMES.MAIN_ROUTES.ORDER,
    module: "order",
  },
  [ROUTE_NAMES.MAIN_ROUTES.EDIT_ORDER]: {
    label: "Edit Order",
    description: "Update an existing order",
    parent: ROUTE_NAMES.MAIN_ROUTES.ORDER,
    module: "order",
  },
  [ROUTE_NAMES.MAIN_ROUTES.ORDER_DETAIL]: {
    label: "Order Details",
    description: "View complete order information",
    parent: ROUTE_NAMES.MAIN_ROUTES.ORDER,
    module: "order",
  },
  [ROUTE_NAMES.MAIN_ROUTES.COMPANIES]: {
    label: "Car Companies",
    description: "Manage vehicle brand listings",
    listing: true,
  },
  [ROUTE_NAMES.MAIN_ROUTES.VEHICLE]: {
    label: "Vehicle",
    description: "Manage vehicle models and types",
    listing: true,
  },
  [ROUTE_NAMES.MAIN_ROUTES.TESTIMONIAL]: {
    label: "Testimonial",
    description: "Manage category testimonial videos",
    listing: true,
    module: "testimonial",
  },
  [ROUTE_NAMES.MAIN_ROUTES.COUPON]: {
    label: "Coupon",
    description: "Manage discount coupons",
    listing: true,
    module: "coupon",
  },
  [ROUTE_NAMES.MAIN_ROUTES.CREATE_COUPON]: {
    label: "Create Coupon",
    description: "Add a new coupon offer",
    parent: ROUTE_NAMES.MAIN_ROUTES.COUPON,
    module: "coupon",
  },
  [ROUTE_NAMES.MAIN_ROUTES.EDIT_COUPON]: {
    label: "Edit Coupon",
    description: "Update an existing coupon",
    parent: ROUTE_NAMES.MAIN_ROUTES.COUPON,
    module: "coupon",
  },
  [ROUTE_NAMES.MAIN_ROUTES.COUPON_DETAIL]: {
    label: "Coupon Details",
    description: "View complete coupon information",
    parent: ROUTE_NAMES.MAIN_ROUTES.COUPON,
    module: "coupon",
  },
  [ROUTE_NAMES.MAIN_ROUTES.UNAUTHORIZED]: {
    label: "Unauthorized",
    hide: true,
  },
  [ROUTE_NAMES.MAIN_ROUTES.FEEDBACK]: {
    label: "Feedback",
    description: "Review customer feedback, complaints, and unresolved items",
    listing: true,
  },
};

const toLabel = (segment: string) =>
  segment
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizePath = (pathname: string) =>
  pathname.replace(/\/+$/, "") || "/";

export const matchBreadcrumbRoute = (pathname: string) => {
  const path = normalizePath(pathname);
  if (BREADCRUMB_ROUTES[path]) {
    return { path, ...BREADCRUMB_ROUTES[path] };
  }

  const parts = path.split("/").filter(Boolean);
  if (parts.length < 2) return null;

  const parentPath = `/${parts.slice(0, -1).join("/")}`;
  const parent = BREADCRUMB_ROUTES[parentPath];
  if (parent?.listing) {
    return {
      path,
      label: toLabel(parts[parts.length - 1]),
      parent: parentPath,
      listing: true,
      module: parent.module,
    };
  }

  return null;
};

const lastListingKey = (module?: string) =>
  module ? `breadcrumb-last:${module}` : "";

export const rememberListingPath = (pathname: string) => {
  const match = matchBreadcrumbRoute(pathname);
  if (!match?.listing || !match.module) return;
  sessionStorage.setItem(lastListingKey(match.module), normalizePath(pathname));
};

export const getParentPath = (match: BreadcrumbRoute) => {
  if (!match.parent) return "";
  const stored = match.module
    ? sessionStorage.getItem(lastListingKey(match.module))
    : null;
  return stored || match.parent;
};
